import React from "react";
import { useTranslation } from "react-i18next";

import { isApiError } from "@/queries/api/apiUtils";
import { Logger } from "@/utils/logger";

type Props = { children: React.ReactNode; fallback?: React.ReactNode };
type State = { hasError: boolean; err?: unknown };

function isAuthError(err: unknown): boolean {
  if (!isApiError(err)) return false;
  return err.status === 401 || err.status === 403;
}

function clearAuthStorage() {
  try {
    sessionStorage.removeItem("pm-refresh");
  } catch {}
  try {
    localStorage.removeItem("pm-user");
  } catch {}
}

function SessionExpiredFallback() {
  const { t } = useTranslation("common");
  return (
    <div className="p-6 text-center">
      <h2 className="text-lg font-semibold text-red-700">
        {t("errors.unauthorized")}
      </h2>
      <p className="mt-2 text-sm text-gray-700">
        {t("errorBoundary.signInPrompt")}
      </p>
      <button
        type="button"
        className="mt-4 inline-block rounded-md bg-garnet px-4 py-2 text-sm font-semibold text-white"
        onClick={() => {
          clearAuthStorage();
          window.location.assign("/auth/login");
        }}
      >
        {t("errorBoundary.signInAgain")}
      </button>
    </div>
  );
}

function DefaultFallback({ err }: { err?: unknown }) {
  const { t } = useTranslation("common");
  if (isAuthError(err)) return <SessionExpiredFallback />;
  return (
    <div className="p-6 text-center">
      <h2 className="text-lg font-semibold text-red-700">
        {t("errorBoundary.title")}
        {typeof err === "string" && err}
      </h2>
      <p className="mt-2 text-sm text-gray-700">
        {t("errorBoundary.description")}
      </p>
      {import.meta.env.DEV && (
        <pre className="mt-3 text-left text-xs bg-black/5 p-3 rounded">
          {String((err as any)?.stack ?? err)}
        </pre>
      )}
    </div>
  );
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };
  error: unknown

  static getDerivedStateFromError(err: unknown) {
    return { hasError: true, err };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    Logger.error("AppErrorBoundary caught error", {
      error: String((error as any)?.message ?? error),
      stack: (error as any)?.stack,
      info,
    });
  }

  render() {
    if (this.state.hasError) {
      if (isAuthError(this.state.err)) {
        return <SessionExpiredFallback />;
      }
      return this.props.fallback ?? <DefaultFallback err={this.state.err} />;
    }
    return this.props.children;
  }
}
